import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { Box, Typography, Button, Divider, Modal, IconButton, Select, MenuItem, Stack, CircularProgress, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { acceptPartialOrder, createBilledInvoice, createCancelInvoice, getCancelReasons } from "../../../../../service/order/orderService";
import PartialAcceptPanel, {
  allUnavailable,
  hasUnavailable,
  Unavailable,
} from "./PartialAcceptPanel";
import { useParams } from "react-router-dom";
import SelectFieldWithSearch from "../../../../../components/Form/SelectFieldWithSearch";
import { Formik, Form } from "formik";
import { notifyError, notifySuccess } from "../../../../../components/ToastContainer/Toast/Toast";
import { green } from "@mui/material/colors";
import vegIcon from "../../../../../Assets/Veg_symbol.svg.png"
import nonVegIcon from "../../../../../Assets/Non_veg_symbol.svg.png"
import eggIcon from "../../../../../Assets/Egg.svg"
import { useOrderCounts } from "../../../Tabs/InvoiceTabs";
import { useAppDispatch } from "../../../../../Redux/hooks";
import { setActiveKey } from "../../../../../Redux/Slices/tabSlice";

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  overflow: 'hidden',
};

interface OrderItem {
  id: number;
  product_id: string;
  name: string;
  qty: number;
  price: string; 
  original_price?: string;
  discount_type?: string;
  discount_value?: string;
  gst_percentage?: string;
  gst_price?: string;
  total_price?: string;
  product_type?: string;
  isVeg?: string;
  /** Tazty buyer line (partial-cancellable orders). */
  buyer_order_item_id?: number | null;
  customer_value?: number | null;
}
interface OrderData {
  preparationTime?: string;
  id: string;
  time: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  address: string;
  totalAmount: string;
  instruction: string;
  items: OrderItem[];
  store_id: number;
  partialCancelAllowed?: boolean;
  /** What the customer pays (buyer order total). */
  grossAmount?: number;
}

const parseISOFormat =(value: string | undefined): number => value ? Number(value.match(/\d+/)?.[0]) : 10;

const getGroupedDisplayItems = (items: OrderItem[]) => {
  const baseProductsMap = new Map<string, OrderItem & { addons?: OrderItem[] }>();
  const processedAddonIds = new Set<number>(); // Track addons already nested

  items.forEach(item => {
    const itemProductIdStr = String(item.product_id); // Ensure product_id is string for startsWith/match

    if (itemProductIdStr.startsWith('a-')) {
      // This is an addon
      // Regex to find the base product ID from an addon ID like 'a-123-456' or 'a-123'
      const baseProductIdMatch = itemProductIdStr.match(/a-(\d+)(?:-\d+)?/);
      if (baseProductIdMatch && baseProductIdMatch[1]) {
        const baseProductId = baseProductIdMatch[1];
        // Try to get the base product from the map first, or find it in the original list
        let foundBaseProduct = baseProductsMap.get(baseProductId);

        if (!foundBaseProduct) {
          // If not in map, try to find the actual base product in the current Order's items
          const originalBaseItem = items.find(bp => !String(bp.product_id).startsWith('a-') && String(bp.product_id) === baseProductId);
          if (originalBaseItem) {
            foundBaseProduct = { ...originalBaseItem, addons: [] };
            baseProductsMap.set(baseProductId, foundBaseProduct);
          }
        }

        if (foundBaseProduct) {
          if (!foundBaseProduct.addons) {
            foundBaseProduct.addons = [];
          }
          foundBaseProduct.addons.push(item);
          processedAddonIds.add(item.id); // Mark this addon as processed
        } else {
          // If no base product is found, treat this addon as a standalone item
          // Use a unique key for standalone addons to avoid overwriting base products
          baseProductsMap.set(`standalone-addon-${item.id}`, { ...item, addons: [] });
          processedAddonIds.add(item.id);
        }
      } else {
        // If addon product_id doesn't match expected pattern, treat as standalone
        baseProductsMap.set(`standalone-addon-${item.id}`, { ...item, addons: [] });
        processedAddonIds.add(item.id);
      }
    } else {
      // This is a base product
      if (!baseProductsMap.has(itemProductIdStr)) {
        baseProductsMap.set(itemProductIdStr, { ...item, addons: [] });
      } else {
        // If it already exists (e.g., an addon might have created an entry), merge
        const existing = baseProductsMap.get(itemProductIdStr);
        baseProductsMap.set(itemProductIdStr, { ...item, addons: existing?.addons || [] });
      }
    }
  });

  // Add any remaining items that were not processed (e.g., standalone items that are not addons)
  items.forEach(item => {
    const itemProductIdStr = String(item.product_id);
    // Check if it's a base product not yet added, or an addon not yet processed
    if ((!itemProductIdStr.startsWith('a-') && !baseProductsMap.has(itemProductIdStr)) ||
      (itemProductIdStr.startsWith('a-') && !processedAddonIds.has(item.id) && !baseProductsMap.has(`standalone-addon-${item.id}`))) {
      baseProductsMap.set(`unprocessed-item-${item.id}`, { ...item, addons: [] });
    }
  });

  return Array.from(baseProductsMap.values());
};

const AcceptOrderModal = ({ open, handleClose, order, fetchOrderData }: { open: boolean, handleClose: () => void, order: OrderData | null, fetchOrderData: () => void }) => {
  const [prepTime, setPrepTime] = useState(10);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { store_id } = useParams();
  // Partial acceptance (Tazty orders marked partial_cancel_allowed only).
  const [unavailable, setUnavailable] = useState<Unavailable>({});
  const [partialReasonId, setPartialReasonId] = useState<number | "">("");

  const handleIncrement = () => setPrepTime(prev => Math.min(prev + 1, 60));
  const handleDecrement = () => setPrepTime(prev => Math.max(prev - 1, 0));

  // Reset prepTime when modal opens
  useEffect(() => {
    if (open) {
      setPrepTime(parseISOFormat(order?.preparationTime) || 10);
      setUnavailable({});
      setPartialReasonId("");
    }
  }, [open]);



  const groupedDisplayItems = useMemo(() => {
    return order ? getGroupedDisplayItems(order.items) : [];
  }, [order]);

  const { moveOrderCount, refreshCallbacksRef } = useOrderCounts();
  const dispatch = useAppDispatch();

  const handleProcessOrder = async () => {
    if (!order) {
      setErrorMessage("No order data available to process.");
      return;
    }

    // Some items unavailable: accept the rest (Seller API accept-partial).
    // Otherwise the normal accept below, exactly as before.
    if (order.partialCancelAllowed && hasUnavailable(unavailable)) {
      if (allUnavailable(order.items ?? [], unavailable)) {
        notifyError("Nothing would be left - cancel the order instead.");
        return;
      }
      if (partialReasonId === "") {
        notifyError("Choose a reason for the unavailable items.");
        return;
      }
      setLoading(true);
      try {
        const cancel_items = Object.entries(unavailable)
          .filter(([, n]) => Number(n) > 0)
          .map(([id, n]) => ({ buyer_order_item_id: Number(id), quantity: Number(n) }));
        const response = await acceptPartialOrder(order.id, {
          cancel_items,
          cancel_reason_id: Number(partialReasonId),
          preparation_time: `PT${prepTime}M`,
        });
        if (response?.data?.success) {
          notifySuccess(`Order ${order.id} accepted without the unavailable items.`);
          moveOrderCount('new', 'preparing');
          fetchOrderData();
          handleClose();
          dispatch(setActiveKey("preparing"));
          window.setTimeout(() => {
            refreshCallbacksRef.current.preparing?.();
          }, 400);
        } else {
          notifyError(response?.data?.message || "Could not accept the order.");
        }
      } catch (error: any) {
        notifyError(error?.response?.data?.message || "Could not accept the order.");
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      // Accept the order with preparation time included in the payload
      const payload = {
        order_id: order.id,
        items: order?.items.map((item) => ({
          item_id: item.id,
          product_id: item.product_id,
          quantity: item.qty,
        })),
        cancel_entire_order: false,
        type: "bill",
        preparation_time: `PT${prepTime}M`
      };

      const response = await createBilledInvoice(payload);

      if (response?.status === 201) {
        notifySuccess(`Order ${order.id} accepted and processed successfully!`);
        
        // Update counts: move from 'new' to 'preparing'
        moveOrderCount('new', 'preparing');
        
        fetchOrderData();
        handleClose();

        // Switch UI to Preparing tab and refresh it so the accepted order shows up immediately.
        dispatch(setActiveKey("preparing"));
        window.setTimeout(() => {
          refreshCallbacksRef.current.preparing?.();
        }, 400);
      } else {
        notifyError(`${response?.status}`);
      }
    } catch (error) {
      notifyError("Failed to process the order. Please try again.");
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        {/* Header Section */}
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: 'transparent',
          p: 3,
          pb: 2,

        }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>Accept Order</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{order?.id} | {order?.time}</Typography> {/* Use optional chaining */}
          </Box>
          <IconButton onClick={handleClose} sx={{ p: 0.5 }}>
            <CloseIcon sx={{ fontSize: '1.2rem', border: '1px solid transparent' }} />
          </IconButton>
        </Box>

        {/* First Divider */}
        <Divider sx={{ my: 0, mx: 0, bgcolor: '#e0e0e0' }} />

        {/* Order Items Section */}
        <Stack sx={{ py: 2, px: 3, maxHeight: '300px', overflowY: 'auto' }}>
          {groupedDisplayItems.length > 0 ? (
            groupedDisplayItems.map((item, index) => (
              <Box
                key={item.product_id || index} 
                display="flex"
                flexDirection="column" 
                alignItems="flex-start"
                mb={1} 
              >
                {/* Main Product Name + Qty and Price */}
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Box display="flex" alignItems="center" gap={1}>
                    {item.product_type === "veg" && (
                      <img src={vegIcon} alt="Veg" style={{ width: 16, height: 16 }} />
                    )}
                    {item.product_type === "non-veg" && (
                      <img src={nonVegIcon} alt="Non-Veg" style={{ width: 16, height: 16 }} />
                    )}
                    {item.product_type === "egg" && (
                      <img src={eggIcon} alt="Egg" style={{ width: 16, height: 16 }} />
                    )}
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                      {item.name} x {item.qty}
                    </Typography>
                  </Box>
                  {/* Right: Price */}
                  <Typography fontWeight={500}>
                    ₹{item.total_price}
                  </Typography>
                </Box>

                {/* Addons Section */}
                {item.addons && item.addons.length > 0 && (
                  <Box sx={{ pl: 3, width: '100%' }}>
                    {item.addons.map((addon, j) => (
                      <Box key={addon.id || j} display="flex" justifyContent="space-between" width="100%">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", pr: 1 }}
                        >
                          • {addon.name} x {addon.qty}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          ₹{addon.total_price}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              No items available
            </Typography>
          )}
        </Stack>

        {order?.partialCancelAllowed && (
          <PartialAcceptPanel
            items={order.items}
            grossAmount={Number(order.grossAmount ?? order.totalAmount ?? 0)}
            unavailable={unavailable}
            onChange={setUnavailable}
            cancelReasonId={partialReasonId}
            onCancelReasonChange={setPartialReasonId}
          />
        )}

        <Divider sx={{ my: 0, mx: 0, bgcolor: '#e0e0e0' }} />

        {/* Set Preparation Time Section */}
        <Box sx={{ 
          px: 3, 
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <Typography fontWeight={600} sx={{ fontSize: "0.95rem" }}>
            Set Preparation Time
          </Typography>
          <Box sx={{ 
            display: "flex", 
            alignItems: "center",
            gap: 1.5,
            border: "1px solid rgb(170, 169, 169)",
            borderRadius: 1,
          }}>
            <IconButton 
              onClick={handleDecrement}
              disabled={prepTime <= 0 || loading}
              size="small"
              sx={{ 
                minWidth: 32,
                width: 32,
                height: 32,
                padding: 0,
                color: "black",
                border: '1px solid transparent',
                '&:hover': { bgcolor: 'transparent', border: '1px solid transparent' },
                '&:disabled': { opacity: 0.5 }
              }}
            >
              <RemoveIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
            <Typography 
              sx={{ 
                fontSize: "1rem", 
                fontWeight: 600,
                color: "#1976d2",
                minWidth: 50,
                textAlign: "center"
              }}
            >
              {prepTime} Min
            </Typography>
            <IconButton 
              onClick={handleIncrement}
              disabled={prepTime >= 60 || loading}
              size="small"
              sx={{ 
                minWidth: 32,
                width: 32,
                height: 32,
                padding: 0,
                color: "black",
                border: '1px solid transparent',
                '&:hover': { bgcolor: 'transparent', border: '1px solid transparent' },
                '&:disabled': { opacity: 0.5 }
              }}
            >
              <AddIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ my: 0, mx: 0, bgcolor: '#e0e0e0' }} />

        <Button
          fullWidth
          variant="contained"
          onClick={handleProcessOrder} 
          disabled={loading || !order} 
          sx={{
            bgcolor: 'black',
            textTransform: 'none',
            py: 1.5,
            borderRadius: 1,
            mb: 3, mt: 3,
            mx: 3,
            width: 'calc(100% - 48px)',
            fontSize: '1rem',
            fontWeight: 'bold',
            '&:hover': { bgcolor: '#333' }
          }}>
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Accept Order'} 
        </Button>
      </Box>
    </Modal>
  );
};


// Cancel Order Modal Component
const CancelOrderModal = ({ open, handleClose, order, fetchOrderData }: { open: boolean, handleClose: () => void, order: OrderData | null, fetchOrderData: () => void }) => {
  const [reasonId, setReasonId] = useState<string>("");
  const [cancelReasons, setCancelReasons] = useState<{ label: string; value: string }[]>([]);
  const { decrementCount } = useOrderCounts();

  const groupedDisplayItems = useMemo(() => {
    return order ? getGroupedDisplayItems(order.items) : [];
  }, [order]);

  useEffect(() => {
    const fetchCancelReasons = async () => {
      try {
        const res = await getCancelReasons();
        if (res?.data?.data) {
          const formatted = res.data.data.map((item: any) => ({
            label: item.reason, // UI display
            value: item.id,    // backend payload id
          }));
          setCancelReasons(formatted);
        }
      } catch (error) {
        console.error("Failed to fetch cancel reasons:", error);
      }
    };

    if (open) {
      fetchCancelReasons();
    }
  }, [open]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            pb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: "1.2rem" }}
            >
              Cancel Order
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8rem" }}
            >
              {order?.id} | {order?.time}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ p: 0.5 }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>

        <Divider sx={{ my: 0, mx: 0, bgcolor: "#e0e0e0" }} />

        {/* Formik Form */}
        <Formik
          initialValues={{ cancelReason: "" }}
          validationSchema={Yup.object({
            cancelReason: Yup.string().required("Please select a reason"),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (!order) {
                notifyError("No order data available to process.");
                return;
              }
              const payload = {
                order_id: order.id,
                items: order?.items.map((item) => ({
                  item_id: item.id,
                  product_id: item.product_id,
                  quantity: item.qty,
                })),
                cancel_reason_id: values.cancelReason,
                cancel_entire_order: false,
                type: "cancel"
              };

              try {
                const response = await createCancelInvoice(payload);
                if (response?.status === 201) {
                  notifySuccess(`Order ${order.id} cancelled successfully!`);
                  
                  // Update counts: decrement 'new' count when order is cancelled
                  decrementCount('new');
                  
                  fetchOrderData();
                  handleClose();
                } else {
                  notifyError(response?.data?.message);
                }
              } catch (error) {
                notifyError(error?.response?.data?.message || "Failed to cancel the order. Please try again.");
                handleClose();
              }

              handleClose();
            } catch (error) {
              console.error("Cancel Failed:", error);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, setFieldValue, errors, touched, isSubmitting }) => (
            <Form>
              {/* Body */}
              <Box sx={{ px: 3, py: 2, backgroundColor: "white" }}>
                <Typography
                  fontWeight={600}
                  sx={{ fontSize: "0.95rem", mb: 1 }}
                >
                  Are you sure to cancel this order?
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  If Yes, Select Reason
                </Typography>

                {/* ✅ SelectFieldWithSearch */}
                <SelectFieldWithSearch
                  label=""
                  name="cancelReason"
                  value={values.cancelReason}
                  // onChange={(val) => setFieldValue("cancelReason", val.target.value)}
                  onChange={(val) => {
                    setFieldValue("cancelReason", val.target.value ?? val);
                  }}
                  options={cancelReasons}
                  placeholder="Select Reason"
                />
              </Box>

              {/* Actions */}
              <Stack
                direction="row"
                spacing={2}
                sx={{ mb: 3, mx: 3, mt: 2 }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClose}
                  sx={{
                    textTransform: "none",
                    py: 1.5,
                    borderRadius: 1,
                    borderColor: "#e0e0e0",
                    color: "black",
                  }}
                  disabled={isSubmitting}
                >
                  Discard
                </Button>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  // disabled={!values.cancelReason || isSubmitting}
                  sx={{
                    bgcolor: "black",
                    textTransform: "none",
                    py: 1.5,
                    borderRadius: 1,
                    color: "white",
                    "&:hover": { bgcolor: "#333" },
                  }}
                >
                  {/* {isSubmitting ? "Cancelling..." : "Cancel"} */}
                  Cancel
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

// Main component that exports both modals
interface ProcessOrderModalsProps {
  acceptModalOpen: boolean;
  handleCloseAcceptModal: () => void;
  cancelModalOpen: boolean;
  handleCloseCancelModal: () => void;
  orderData: OrderData | null; // Pass the processed order data
  fetchOrderData: () => void; // Function to refresh orders after processing
}

const ProcessOrderModals = ({
  acceptModalOpen,
  handleCloseAcceptModal,
  cancelModalOpen,
  handleCloseCancelModal,
  orderData,
  fetchOrderData
}: ProcessOrderModalsProps) => {

  return (
    <>
      <AcceptOrderModal
        open={acceptModalOpen}
        handleClose={handleCloseAcceptModal}
        order={orderData}
        fetchOrderData={fetchOrderData} // Pass the function to refresh orders after processing
      />
      <CancelOrderModal
        open={cancelModalOpen}
        handleClose={handleCloseCancelModal}
        order={orderData}
        fetchOrderData={fetchOrderData} // Pass the function to refresh orders after processing
      />
    </>
  );
};

export default ProcessOrderModals;